// Quick-actions menu surfaced from the reservation detail card.
//
// Houses the operator-driven affordances that don't fit on the always-
// visible toolbar: pin level controls, +15/+30/+45 delay quick-actions,
// "cambiar mesa…" entry (opens the TableExplorer drawer), and the
// mid-service "mover de mesa…" flow with its required unpin confirm.
//
// Mounted as a popover the parent toggles from a ⋯ button next to the
// status pill. Keeps its own optimistic UI state so a network failure
// surfaces as an inline error without leaving the menu in a stale
// state. Cache invalidation comes from the API helpers themselves.

import { type ReactNode, useState } from "react";
import { Clock, Lock, MapPin, Minus, MoreVertical, Pin, Plus, Unlock, Users } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  apiDelayReservation,
  apiMoveSeated,
  apiSetPinLevel,
  apiUpdateReservation,
} from "@/lib/reservation-api";
import { useAppStore } from "@/store/store";
import type { Reservation, ReservationPinLevel } from "@/store/types";

interface Props {
  reservation: Reservation;
  /** Optional handler the parent provides to open the TableExplorer
   * drawer. Modes:
   *   - ``reassign``      — BOOKED, swap mesa before sitting
   *   - ``seat-override`` — BOOKED, pick a mesa AND seat in one action
   *   - ``mid-service``   — SEATED, relocate a party already at table */
  onOpenExplorer?: (mode: "reassign" | "seat-override" | "mid-service") => void;
}

const PIN_OPTIONS: Array<{ value: ReservationPinLevel; label: string; icon: ReactNode }> = [
  { value: "none", label: "Flexible", icon: <Unlock className="size-3.5" /> },
  { value: "zone", label: "A zona", icon: <MapPin className="size-3.5" /> },
  { value: "table", label: "A mesa", icon: <Lock className="size-3.5" /> },
];

const DELAY_OPTIONS = [15, 30, 45] as const;

export function ReservationContextMenu({ reservation, onOpenExplorer }: Props) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function changePin(level: ReservationPinLevel) {
    if (level === reservation.pinLevel) return;
    setBusy(`pin:${level}`);
    setError(null);
    try {
      await apiSetPinLevel(reservation.id, level);
      setOpen(false);
    } catch (e) {
      setError(formatError(e, "No se pudo cambiar el pin"));
    } finally {
      setBusy(null);
    }
  }

  async function delay(minutes: number) {
    setBusy(`delay:${minutes}`);
    setError(null);
    try {
      await apiDelayReservation(reservation.id, minutes);
      setOpen(false);
    } catch (e) {
      setError(formatError(e, "No se pudo retrasar la reserva"));
    } finally {
      setBusy(null);
    }
  }

  function openExplorer(mode: "reassign" | "seat-override" | "mid-service") {
    setOpen(false);
    onOpenExplorer?.(mode);
  }

  async function changePax(nextPax: number) {
    if (nextPax === reservation.pax || nextPax < 1) return;
    setBusy(`pax:${nextPax}`);
    setError(null);
    try {
      await apiUpdateReservation(reservation.id, { pax: nextPax });
      // Optimistic local patch so the card reflects the bump
      // immediately. React Query refetch reconciles after.
      useAppStore.setState((state) => ({
        reservations: state.reservations.map((r) =>
          r.id === reservation.id ? { ...r, pax: nextPax } : r,
        ),
      }));
    } catch (e) {
      setError(formatError(e, "No se pudo cambiar comensales"));
    } finally {
      setBusy(null);
    }
  }

  const isBooked = reservation.status === "reservada";
  const isSeated = reservation.status === "sentado";
  // Walk-ins skip the pin/delay/edit affordances: they are physically
  // sat with no booked window to manipulate. The only meaningful
  // action on them is mid-service relocation.
  const isWalkIn = reservation.origin === "walkin";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className="flex size-7 items-center justify-center rounded-full text-text-secondary hover:bg-gray-100"
        aria-label="Más acciones"
      >
        <MoreVertical className="size-4" />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2 text-sm">
        {/* Pin section — hidden for walk-ins (already physically anchored). */}
        {!isWalkIn && (
          <>
            <div className="px-2 pt-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-text-muted">
              <Pin className="inline size-3 mr-1" />
              Pin del motor
            </div>
            <div className="grid grid-cols-3 gap-1 px-1 pb-2">
              {PIN_OPTIONS.map((opt) => {
                const active = reservation.pinLevel === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    disabled={busy !== null}
                    onClick={() => changePin(opt.value)}
                    className={
                      "flex flex-col items-center gap-0.5 rounded-md py-1.5 text-[11px] font-medium transition " +
                      (active
                        ? "bg-orange-500 text-white"
                        : "bg-gray-50 text-text-secondary hover:bg-gray-100")
                    }
                  >
                    {opt.icon}
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* Pax bump — only for walk-ins. Bookings expose pax via the
            full Editar form (date, time, zone, mesa all in one
            place). Walk-ins don't have a date/time to negotiate so a
            mini +/- widget is enough for the common "friend showed
            up" case. */}
        {isWalkIn && (
          <>
            <div className="px-2 pt-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-text-muted">
              <Users className="inline size-3 mr-1" />
              Comensales
            </div>
            <div className="flex items-center justify-between gap-1.5 px-1 pb-2">
              <button
                type="button"
                disabled={busy !== null || reservation.pax <= 1}
                onClick={() => changePax(reservation.pax - 1)}
                className="flex size-7 items-center justify-center rounded-md bg-gray-50 text-text-secondary transition hover:bg-gray-100 disabled:opacity-40"
                aria-label="Quitar 1 comensal"
              >
                <Minus className="size-3.5" />
              </button>
              <span className="text-sm font-bold tabular-nums text-text-primary">
                {reservation.pax}
              </span>
              <button
                type="button"
                disabled={busy !== null}
                onClick={() => changePax(reservation.pax + 1)}
                className="flex size-7 items-center justify-center rounded-md bg-gray-50 text-text-secondary transition hover:bg-gray-100"
                aria-label="Agregar 1 comensal"
              >
                <Plus className="size-3.5" />
              </button>
            </div>
            <p className="px-2 pb-2 text-[10px] text-text-muted">
              Para ampliar mesas o juntarlas, usa "Mover de mesa".
            </p>
          </>
        )}

        {/* Time section */}
        {isBooked && (
          <>
            <div className="px-2 pt-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-text-muted">
              <Clock className="inline size-3 mr-1" />
              Retrasar
            </div>
            <div className="grid grid-cols-3 gap-1 px-1 pb-2">
              {DELAY_OPTIONS.map((m) => (
                <button
                  key={m}
                  type="button"
                  disabled={busy !== null}
                  onClick={() => delay(m)}
                  className="rounded-md bg-gray-50 py-1.5 text-[11px] font-medium text-text-secondary hover:bg-gray-100"
                >
                  +{m}m
                </button>
              ))}
            </div>
          </>
        )}

        {/* Tables section */}
        <div className="px-2 pt-1 pb-1 text-[10px] font-medium uppercase tracking-wide text-text-muted">
          Mesa
        </div>
        <div className="flex flex-col gap-0.5 px-1 pb-1">
          {isBooked && onOpenExplorer && (
            <>
              <button
                type="button"
                onClick={() => openExplorer("reassign")}
                className="rounded-md px-2 py-1.5 text-left text-[12px] text-text-secondary hover:bg-gray-50"
              >
                Cambiar de mesa…
              </button>
              <button
                type="button"
                onClick={() => openExplorer("seat-override")}
                className="rounded-md px-2 py-1.5 text-left text-[12px] text-text-secondary hover:bg-gray-50"
              >
                Sentar en otra mesa…
              </button>
            </>
          )}
          {isSeated && onOpenExplorer && (
            <button
              type="button"
              onClick={() => openExplorer("mid-service")}
              className="rounded-md px-2 py-1.5 text-left text-[12px] text-text-secondary hover:bg-gray-50"
            >
              Mover de mesa (mid-service)…
            </button>
          )}
        </div>

        {error && (
          <p className="mt-1 px-2 text-[11px] text-rose-600" role="alert">
            {error}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Mid-service move flow: ensure the SEATED reservation isn't pinned to
 * its current tables, then call ``apiMoveSeated``. The caller decides
 * which tables to target (typically from a TableExplorer pick).
 *
 * Returns the touched reservation on success. Throws on failure so the
 * caller can render an inline error in its own surface — the explorer
 * drawer is the natural home for that. */
export async function performMidServiceMove(
  reservation: Reservation,
  newTableIds: string[],
  moves: Array<{ reservation_id: string; tables: string[] }> = [],
): Promise<unknown> {
  // The backend's /move-seated/ endpoint drops the TABLE pin
  // atomically inside the same transaction as the move + re-pin.
  // The old client-side "unpin first, then move" trick was a split-
  // API call that could leave the reservation SEATED-but-unpinned on
  // a partial failure.
  return apiMoveSeated(reservation.id, newTableIds, moves);
}

function formatError(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "message" in error) {
    return String((error as { message: unknown }).message);
  }
  return fallback;
}
